import React, { useState, useEffect, useMemo } from 'react';
import { AppConfiguration } from 'config';
import MetaTags from 'react-meta-tags';
import GenericBreadcrumb from 'components/Common/GenericBreadcrumb';
import { Container, Row, Col } from 'reactstrap';
import AccordionItem from 'pages/Campaign/CommonComponents/AccordionItem/AccordionItem';
import ApiFields from '../integration-components/api-fields/ApiFields';
import ResponseType from '../integration-components/response-type/ResponseType';
import TestIntegration from '../integration-components/test-integration/TestIntegration';
import Connector from '../integration-components/connector/Connector';
import { INITIAL_INTEGRATION, integration_accordion_state, MODE, NAME_OF_INTEGRATIONS, REQUIRED_FEATURE_FLAGS } from 'helpers/sf_constants';
import { getLeadIntegrationMacrosForEdit, uiStateForLeadIntegrationMacros, addIsEnumToExistingMacros } from 'helpers/sf_utils';
import { useDispatch, useSelector } from 'react-redux';
import { getIntegrations, getIntegrationsSampleLeads, getLeadIntegrationDefaultPayloadValue, getSfLeadIntegrationMacroCategories, updateIntegration } from 'store/integration/actions';
import { getAccountIntegrationsV2, getLeadIntegrationDetailsV2 } from 'store/integration_v2/actions';
import { useParams } from 'react-router-dom';
import RequestHeaders from '../integration-components/request-headers/RequestHeaders';
import AdminSettings from '../integration-components/admin-settings/AdminSettings'
import useQuery from 'hooks/useQuery';
import ToastAlert, { ToastAlertIconType } from 'components/Common/ToastAlert/ToastAlert';
import { bsh_isAdminUserDetailsPresent } from 'helpers/sf_browser_storage_helper';
import useFeatureFlags from 'hooks/useFeatureFlags';
import { filterIntegrationMacros } from 'helpers/sf_integeration_helper';
import useSingleActionDispatcher from 'hooks/useSingleActionDispatcher';
import useSicCodeTargeting from 'hooks/useSicCodeTargeting';
import useIsLeadIntegrationsV2Enabled from 'hooks/useIsLeadIntegrationsV2Enabled';
import useIntegrationPermissions from 'hooks/useIntegrationPermissions';

const STR_INTEGRATION_COMPONENT_UPDATING_MSG = 'Integration component is updating.'
const STR_INTEGRATION_READ_ONLY_MSG = 'You have view-only access to this integration. Ask an account owner for Manage Integrations to make changes.'

// BM-9524: without Manage Integrations every section is shown read-only. A disabled
// fieldset disables every input and button inside it (save, test, next), while the
// accordion headers, which sit outside it, still open and close.
const ReadOnlySection = ({ readOnly, children }) => (
    readOnly ? <fieldset disabled className="border-0 p-0 m-0">{children}</fieldset> : children
);

const EditIntegration = () => {
    // STATE
    const [initialIntegrationState, setInitialIntegrationState] = useState({ ...INITIAL_INTEGRATION });
    // STATE
    const [integrationAccordionState, setIntegrationAccordionState] = useState({ ...integration_accordion_state });
    const [accordionValidationState, setAccordionValidationState] = useState({ ...integration_accordion_state, [NAME_OF_INTEGRATIONS.CONNECTOR.ID]: true });
    const [activeLeadTypeId, setActiveLeadTypeId] = useState(1)

    const dispatch = useDispatch();
    const { integration_id } = useParams();

    const query = useQuery();
    const openAccordionId = query.get('open_accordion');
    const [isFeatureFlagActive, areFeatureFlagsLoaded] = useFeatureFlags();
    const isV2IntegrationsEnabled = useIsLeadIntegrationsV2Enabled();
    const { canManage } = useIntegrationPermissions();
    const readOnly = !canManage;

    // use Selector
    const {
        current_account,
        integrations,
        sf_lead_integration_macros,
        sf_lead_integration_macro_categories,
        integration_loading,
        lead_integration_logs,
        v2_account_integrations,
        v2_details_by_id,
        v2_account_integrations_loading,
    } = useSelector((state) => ({
        current_account: state.accounts.current_account,
        integrations: state.Integration.integrations,
        sf_lead_integration_macros: state.Integration.sf_lead_integration_macros,
        sf_lead_integration_macro_categories: state.Integration.sf_lead_integration_macro_categories,
        integration_loading: state.Integration.loading,
        lead_integration_logs: state.Integration.lead_integration_logs,
        v2_account_integrations: state.IntegrationV2.account_integrations,
        v2_details_by_id: state.IntegrationV2.details_by_id,
        v2_account_integrations_loading: state.IntegrationV2.account_integrations_loading,
    }));

    const sicTargetingFlags = useSicCodeTargeting({ current_account });

    const current_lead_integration_logs = useMemo(() => {
        return lead_integration_logs.filter(li_log => li_log.lead_integration_id == integration_id);
    }, [integration_id, lead_integration_logs])

    useSingleActionDispatcher(getSfLeadIntegrationMacroCategories());

    useEffect(() => {
        // fetch integration test logs by id
        if (integration_id) {
            dispatch(getLeadIntegrationDefaultPayloadValue({ id: integration_id }));
            if (isV2IntegrationsEnabled) {
                dispatch(getLeadIntegrationDetailsV2(integration_id));
            }
        }
    }, [integration_id, isV2IntegrationsEnabled])

    useEffect(() => {
        if (!areFeatureFlagsLoaded) return
        if (openAccordionId) {
            integrationAccordionToggle(openAccordionId)
        }
        if (isV2IntegrationsEnabled) {
            if (!v2_account_integrations?.length && !v2_account_integrations_loading) {
                dispatch(getAccountIntegrationsV2());
            }
        } else {
            dispatch(getIntegrations());
        }
        dispatch(getIntegrationsSampleLeads());
    }, [areFeatureFlagsLoaded, openAccordionId, isV2IntegrationsEnabled])

    useEffect(() => {
        if (integrations || v2_account_integrations) {
            const lead_integration = isV2IntegrationsEnabled
                ? (v2_details_by_id?.[integration_id] || v2_account_integrations?.find(int => int.id.toString() === integration_id.toString()))
                : integrations.find(int => int.id.toString() === integration_id.toString());
            const lead_integration_macros = uiStateForLeadIntegrationMacros(sf_lead_integration_macros, activeLeadTypeId);
            const current_macros = (lead_integration?.lead_integration_macros || []);
            const current_macros_with_is_enum = addIsEnumToExistingMacros(current_macros, sf_lead_integration_macros)
            const new_macros = getLeadIntegrationMacrosForEdit(current_macros_with_is_enum, lead_integration_macros)
            setInitialIntegrationState({ lead_integration: { ...lead_integration, lead_integration_macros: new_macros } })
        }
    }, [integrations, v2_account_integrations, v2_details_by_id, isV2IntegrationsEnabled, integration_id, sf_lead_integration_macros, activeLeadTypeId]);

    const loading = isV2IntegrationsEnabled ? v2_account_integrations_loading : integration_loading;


    // HANDLER
    const integrationAccordionToggle = (accordion) => {
        setIntegrationAccordionState({ ...integration_accordion_state, [accordion]: !integrationAccordionState[accordion] });
    };

    // HANDLER
    const onSubmitHandler = (initial_state, section_id, set_loading = true) => {

        dispatch(updateIntegration({
            ...initial_state,
            id: integration_id,
            lead_integration: { ...initial_state.lead_integration }
        },
            set_loading
        ))
    }

    return (
        <div className="page-content" id="page-content-wrapper">
            <MetaTags>
                <title>{readOnly ? 'View Integration' : 'Edit Integration'} | {AppConfiguration.WEBSITE}</title>
            </MetaTags>
            <Container fluid>
                <GenericBreadcrumb breadcrumbItem={readOnly ? "View Integration" : "Edit Integration"} backOption={true} title="Integrations" backLink={'/integrations'} />
                {readOnly && (
                    <Row>
                        <Col lg={12}>
                            <div className="alert alert-info" role="alert">{STR_INTEGRATION_READ_ONLY_MSG}</div>
                        </Col>
                    </Row>
                )}
                <Row>
                    <Col lg={12}>
                        <div className="accordion" id="integrations">
                            {/* CONNECTOR ACCORDION START */}
                            <AccordionItem
                                icon="bx-cog"
                                name={NAME_OF_INTEGRATIONS.CONNECTOR.NAME}
                                sectionName={integrationAccordionState[NAME_OF_INTEGRATIONS.CONNECTOR.ID]}
                                functionName={() => integrationAccordionToggle(NAME_OF_INTEGRATIONS.CONNECTOR.ID)}
                                clildren={<ReadOnlySection readOnly={readOnly}><Connector
                                    integration_id={integration_id}
                                    mode={MODE.EDIT}
                                    initialState={initialIntegrationState}
                                    onSubmitState={(initial_state) => onSubmitHandler(initial_state, NAME_OF_INTEGRATIONS.CONNECTOR.ID)}
                                    openNextAccordion={() => integrationAccordionToggle(NAME_OF_INTEGRATIONS.REQUEST_HEADERS.ID)}
                                /></ReadOnlySection>}
                            />
                            {/* CONNECTOR ACCORDION END */}

                            {/* REQUEST_HEADERS ACCORDION START */}
                            <AccordionItem
                                icon="bx-detail"
                                name={NAME_OF_INTEGRATIONS.REQUEST_HEADERS.NAME}
                                sectionName={integrationAccordionState[NAME_OF_INTEGRATIONS.REQUEST_HEADERS.ID]}
                                functionName={() => {
                                    if (!loading) {
                                        integrationAccordionToggle(NAME_OF_INTEGRATIONS.REQUEST_HEADERS.ID)
                                    } else {
                                        ToastAlert.fire({ icon: ToastAlertIconType.WARNING, title: STR_INTEGRATION_COMPONENT_UPDATING_MSG })
                                    }
                                }}
                                clildren={<ReadOnlySection readOnly={readOnly}><RequestHeaders
                                    integration_id={integration_id}
                                    mode={MODE.EDIT}
                                    initialState={initialIntegrationState}
                                    onSubmitState={(initial_state) => onSubmitHandler(initial_state, NAME_OF_INTEGRATIONS.REQUEST_HEADERS.ID)}
                                    openNextAccordion={() => {
                                        integrationAccordionToggle(NAME_OF_INTEGRATIONS.API_FIELDS.ID)
                                        setAccordionValidationState({ ...accordionValidationState, [NAME_OF_INTEGRATIONS.REQUEST_HEADERS.ID]: true })
                                    }}
                                /></ReadOnlySection>}
                            />
                            {/* REQUEST_HEADERS ACCORDION END */}

                            {/* API_FIELDS ACCORDION START */}
                            <AccordionItem
                                icon="bx-git-branch"
                                name={NAME_OF_INTEGRATIONS.API_FIELDS.NAME}
                                sectionName={integrationAccordionState[NAME_OF_INTEGRATIONS.API_FIELDS.ID]}
                                functionName={() => {
                                    if (!loading) {
                                        integrationAccordionToggle(NAME_OF_INTEGRATIONS.API_FIELDS.ID)
                                    } else {
                                        ToastAlert.fire({ icon: ToastAlertIconType.WARNING, title: STR_INTEGRATION_COMPONENT_UPDATING_MSG })
                                    }
                                }}
                                clildren={<ApiFields
                                    integration_id={integration_id}
                                    mode={MODE.EDIT}
                                    readOnly={readOnly}
                                    updateActiveLeadTypeId={(data) => setActiveLeadTypeId(data)}
                                    initialState={initialIntegrationState}
                                    onSubmitState={(initial_state, set_loading) => onSubmitHandler(initial_state, NAME_OF_INTEGRATIONS.API_FIELDS.ID, set_loading)}
                                    sf_lead_integration_macros={sf_lead_integration_macros}
                                    sf_lead_integration_macro_categories={filterIntegrationMacros(sf_lead_integration_macro_categories, current_account?.internal, sicTargetingFlags, bsh_isAdminUserDetailsPresent())}
                                    openNextAccordion={() => {
                                        integrationAccordionToggle(NAME_OF_INTEGRATIONS.RESPONSE_TYPE.ID)
                                        setAccordionValidationState({ ...accordionValidationState, [NAME_OF_INTEGRATIONS.API_FIELDS.ID]: true })
                                    }}
                                />}
                            />
                            {/* API_FIELDS ACCORDION END */}

                            {/* RESPONSE_TYPE ACCORDION START */}
                            <AccordionItem
                                icon="bx-reply"
                                name={NAME_OF_INTEGRATIONS.RESPONSE_TYPE.NAME}
                                sectionName={integrationAccordionState[NAME_OF_INTEGRATIONS.RESPONSE_TYPE.ID]}
                                functionName={() => {
                                    if (!loading) {
                                        integrationAccordionToggle(NAME_OF_INTEGRATIONS.RESPONSE_TYPE.ID)
                                    } else {
                                        ToastAlert.fire({ icon: ToastAlertIconType.WARNING, title: STR_INTEGRATION_COMPONENT_UPDATING_MSG })
                                    }
                                }}
                                clildren={<ReadOnlySection readOnly={readOnly}><ResponseType
                                    integration_id={integration_id}
                                    mode={MODE.EDIT}
                                    initialState={initialIntegrationState}
                                    onSubmitState={(initial_state) => onSubmitHandler(initial_state, NAME_OF_INTEGRATIONS.RESPONSE_TYPE.ID)}
                                    openNextAccordion={() => {
                                        integrationAccordionToggle(NAME_OF_INTEGRATIONS.TEST_INTEGRATION.ID)
                                        setAccordionValidationState({ ...accordionValidationState, [NAME_OF_INTEGRATIONS.RESPONSE_TYPE.ID]: true })
                                    }}
                                /></ReadOnlySection>}
                            />
                            {/* RESPONSE_TYPE ACCORDION END */}

                            {/* TEST_INTEGRATION ACCORDION START */}
                            <AccordionItem
                                icon="bx-rocket"
                                name={NAME_OF_INTEGRATIONS.TEST_INTEGRATION.NAME}
                                sectionName={integrationAccordionState[NAME_OF_INTEGRATIONS.TEST_INTEGRATION.ID]}
                                functionName={() => {
                                    if (!loading) {
                                        integrationAccordionToggle(NAME_OF_INTEGRATIONS.TEST_INTEGRATION.ID)
                                    } else {
                                        ToastAlert.fire({ icon: ToastAlertIconType.WARNING, title: STR_INTEGRATION_COMPONENT_UPDATING_MSG })
                                    }
                                }}
                                clildren={<ReadOnlySection readOnly={readOnly}><TestIntegration
                                    integration_id={integration_id}
                                    mode={MODE.EDIT}
                                    activeLeadTypeId={parseInt(activeLeadTypeId)}
                                    initialState={initialIntegrationState}
                                    onSubmitState={(initial_state) => onSubmitHandler(initial_state, NAME_OF_INTEGRATIONS.TEST_INTEGRATION.ID)}
                                    openNextAccordion={() => {
                                        integrationAccordionToggle(NAME_OF_INTEGRATIONS.ADMIN_SETTINGS.ID)
                                        setAccordionValidationState({ ...accordionValidationState, [NAME_OF_INTEGRATIONS.TEST_INTEGRATION.ID]: true })
                                    }}
                                    leadIntegrationLogs={current_lead_integration_logs}
                                /></ReadOnlySection>}
                            />
                            {/* TEST_INTEGRATION ACCORDION END */}

                            {/* ADMIN_SETTINGS ACCORDION START */}
                            {isFeatureFlagActive(REQUIRED_FEATURE_FLAGS.ADMIN_SPECIFIC_SETTINGS_LEAD_INTEGRATION) && bsh_isAdminUserDetailsPresent() && <AccordionItem
                                icon="bx-user"
                                name={NAME_OF_INTEGRATIONS.ADMIN_SETTINGS.NAME}
                                sectionName={integrationAccordionState[NAME_OF_INTEGRATIONS.ADMIN_SETTINGS.ID]}
                                functionName={() => {
                                    if (!loading) {
                                        integrationAccordionToggle(NAME_OF_INTEGRATIONS.ADMIN_SETTINGS.ID)
                                    } else {
                                        ToastAlert.fire({ icon: ToastAlertIconType.WARNING, title: STR_INTEGRATION_COMPONENT_UPDATING_MSG })
                                    }
                                }}
                                clildren={<AdminSettings
                                    integration_id={integration_id}
                                    mode={MODE.EDIT}
                                    initialState={initialIntegrationState}
                                    onSubmitState={(initial_state) => {
                                        integrationAccordionToggle(NAME_OF_INTEGRATIONS.ADMIN_SETTINGS.ID)
                                        onSubmitHandler(initial_state, NAME_OF_INTEGRATIONS.ADMIN_SETTINGS.ID)
                                    }}
                                    openNextAccordion={() => {
                                        integrationAccordionToggle(NAME_OF_INTEGRATIONS.ADMIN_SETTINGS.ID)
                                        setAccordionValidationState({ ...accordionValidationState, [NAME_OF_INTEGRATIONS.ADMIN_SETTINGS.ID]: true })
                                    }}
                                />}
                            />}
                            {/* ADMIN_SETTINGS ACCORDION END */}
                        </div>
                    </Col>
                </Row>
            </Container>
        </div>
    );
}

export default EditIntegration